import { useEffect, useState } from 'react';

import { fg } from '@confluence/feature-gating';

const isStickyInViewMode = (tableElement: HTMLElement | null) => {
	return Boolean(tableElement?.getAttribute('mode') === 'stick');
};

const isStickyInEditMode = (tableElement: HTMLElement | null) => {
	return Boolean(tableElement?.classList.contains('pm-table-sticky'));
};

/**
 * useEffect to handle sticky comment behavior when attached to a sticky table header.
 * As the user scrolls, the IntersectionObserver triggers the callback the moment the table header starts/stops being sticky
 */
export const useShouldCommentSidebarBeSticky = ({
	annotationElement,
	isEditor,
}: {
	annotationElement?: HTMLElement | null;
	isEditor: boolean;
}) => {
	const classToCheck = '.pm-table-container';

	let elementToObserve: HTMLElement | null | undefined = undefined;

	// elements to observe, these are different than elements that determine if the table is sticky
	if (isEditor) {
		elementToObserve = annotationElement
			?.closest(classToCheck)
			?.parentElement?.querySelector('.pm-table-sticky-sentinel-top');
	} else {
		elementToObserve = annotationElement?.closest(classToCheck);
	}

	// check if table is already sticky
	const initialStickyState = elementToObserve
		? isEditor
			? isStickyInEditMode(elementToObserve.parentElement)
			: isStickyInViewMode(elementToObserve.parentElement)
		: false;

	const [isSticky, setIsSticky] = useState(
		fg('confluence_frontend_remove_duplicate_sidebar_logic') && initialStickyState,
	);

	useEffect(() => {
		if (!fg('confluence_frontend_remove_duplicate_sidebar_logic')) {
			return;
		}

		// Create mutation observer to track class changes
		const stickyTableHeaderObserver = new MutationObserver((mutationsList) => {
			mutationsList.forEach((mutation) => {
				if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
					const target = mutation.target as HTMLElement;
					if ((isEditor && isStickyInEditMode(target)) || isStickyInViewMode(target)) {
						setIsSticky(true);
					} else {
						setIsSticky(false);
					}
				}
			});
		});

		// Start observing class changes on the parent element
		if (elementToObserve?.parentElement) {
			stickyTableHeaderObserver.observe(elementToObserve.parentElement, {
				attributes: true,
				attributeFilter: ['class'],
			});
		}

		// Clean up all observers
		return () => {
			stickyTableHeaderObserver.disconnect();
		};
	}, [elementToObserve?.parentElement, isEditor]);

	useEffect(() => {
		if (!fg('confluence_frontend_remove_duplicate_sidebar_logic')) {
			return;
		}

		setIsSticky(initialStickyState);
	}, [initialStickyState]);

	// if annotation element is inside a nested table, then return false
	if (annotationElement?.closest('.pm-table-nested-table-with-controls')) {
		return { isSticky: false };
	}

	// if annotation element is not inside a table header, then return false
	if (!annotationElement?.closest('th')) {
		return { isSticky: false };
	}

	// Remove debug console logs
	return { isSticky };
};
